import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Flame, Trophy, Medal } from "lucide-react";
import { PremiumBadge } from "@/components/profile/PremiumBadge";
import { useAuth } from "@/hooks/useAuth";
import { useGuest } from "@/contexts/GuestContext";
import { useNavigate } from "react-router-dom";
import { startOfMonth, endOfMonth } from "date-fns";

export const StreakLeaderboard = () => {
  const { user } = useAuth();
  const { isGuest } = useGuest();
  const navigate = useNavigate();

  // Don't show anything for guests
  if (isGuest) return null;

  const { data: leaderboard = [], isLoading } = useQuery({
    queryKey: ['monthly-event-leaderboard'],
    queryFn: async () => {
      const now = new Date();
      const monthStart = startOfMonth(now).toISOString();
      const monthEnd = endOfMonth(now).toISOString();

      // Get event attendance counts for this month grouped by user
      const { data: attendances, error } = await supabase
        .from('event_attendances')
        .select('user_id')
        .eq('status', 'attended')
        .gte('checked_in_at', monthStart)
        .lte('checked_in_at', monthEnd);

      if (error) throw error;

      // Count events per user
      const userEventCounts = (attendances || []).reduce((acc: Record<string, number>, curr) => {
        acc[curr.user_id] = (acc[curr.user_id] || 0) + 1;
        return acc;
      }, {});

      // Get unique user IDs with event counts
      const userIds = Object.keys(userEventCounts);
      
      if (userIds.length === 0) return [];

      // Fetch user profiles
      const { data: profiles } = await supabase
        .from('profiles')
        .select('id, username, full_name, avatar_url, subscription_tier, subscription_status, is_founding_member')
        .in('id', userIds);

      if (!profiles) return [];

      // Combine and sort
      const leaderboardData = profiles
        .map(profile => ({
          ...profile,
          eventCount: userEventCounts[profile.id] || 0
        }))
        .sort((a, b) => b.eventCount - a.eventCount)
        .slice(0, 3);

      return leaderboardData;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes - leaderboard doesn't change frequently
    gcTime: 10 * 60 * 1000,
  });

  const getRankIcon = (index: number) => {
    switch (index) {
      case 0:
        return <Trophy className="w-5 h-5 text-yellow-500" />;
      case 1:
        return <Medal className="w-5 h-5 text-gray-400" />;
      case 2:
        return <Medal className="w-5 h-5 text-amber-600" />;
      default:
        return <span className="text-sm font-bold text-muted-foreground">#{index + 1}</span>;
    }
  };

  const getRankBadgeColor = (index: number) => {
    switch (index) {
      case 0:
        return "bg-gradient-to-r from-yellow-400 to-yellow-600 text-white";
      case 1:
        return "bg-gradient-to-r from-gray-300 to-gray-500 text-white";
      case 2:
        return "bg-gradient-to-r from-amber-500 to-amber-700 text-white";
      default:
        return "bg-secondary";
    }
  };

  if (isLoading) {
    return (
      <Card className="p-6 card-shadow">
        <div className="flex items-center gap-2 mb-4">
          <Skeleton className="w-5 h-5 rounded" />
          <Skeleton className="h-6 w-40" />
        </div>
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="flex items-center justify-between p-3 rounded-lg bg-secondary/30">
              <div className="flex items-center gap-3">
                <Skeleton className="w-8 h-8 rounded-full" />
                <Skeleton className="w-10 h-10 rounded-full" />
                <div className="space-y-1">
                  <Skeleton className="h-4 w-24" />
                  <Skeleton className="h-3 w-32" />
                </div>
              </div>
              <Skeleton className="h-5 w-12 rounded-full" />
            </div>
          ))}
        </div>
      </Card>
    );
  }

  if (leaderboard.length === 0) {
    return null;
  }

  return (
    <Card className="p-6 card-shadow">
      <div className="flex items-center gap-2 mb-4">
        <Flame className="w-5 h-5 text-orange-500" />
        <h2 className="font-display text-lg font-semibold text-foreground">Streak Leaderboard</h2>
      </div>

      <div className="space-y-3">
        {leaderboard.map((profile: any, index) => {
          const isCurrentUser = profile.id === user?.id;
          
          return (
            <button
              key={profile.id}
              onClick={() => navigate(`/profile/${profile.id}`)}
              className={`w-full flex items-center justify-between p-3 rounded-lg transition-colors cursor-pointer ${
                isCurrentUser 
                  ? 'bg-primary/10 border border-primary/30' 
                  : 'bg-secondary/30 hover:bg-secondary/50'
              }`}
            >
              <div className="flex items-center gap-3">
                <div className={`w-8 h-8 flex items-center justify-center rounded-full ${getRankBadgeColor(index)}`}>
                  {getRankIcon(index)}
                </div>
                
                <Avatar className="w-10 h-10">
                  <AvatarImage src={profile.avatar_url || undefined} />
                  <AvatarFallback>
                    {(profile.full_name || profile.username || 'U')[0].toUpperCase()}
                  </AvatarFallback>
                </Avatar>

                <div className="text-left">
                  <div className="flex items-center gap-1">
                    <p className={`text-sm font-semibold ${isCurrentUser ? 'text-primary' : 'text-foreground'}`}>
                      {profile.full_name || profile.username}
                      {isCurrentUser && <span className="ml-1 text-xs">(You)</span>}
                    </p>
                    {(profile.subscription_tier === 'premium' && profile.subscription_status === 'active') && (
                      <PremiumBadge isFoundingMember={profile.is_founding_member} size="sm" />
                    )}
                  </div>
                  <div className="flex items-center gap-1 mt-0.5">
                    <Flame className="w-3 h-3 text-orange-500" />
                    <span className="text-xs text-muted-foreground">
                      {profile.eventCount} event{profile.eventCount !== 1 ? 's' : ''} this month
                    </span>
                  </div>
                </div>
              </div>

              {index < 3 && (
                <Badge variant="outline" className={`text-xs ${getRankBadgeColor(index)} border-0`}>
                  Top {index + 1}
                </Badge>
              )}
            </button>
          );
        })}
      </div>

      <p className="text-xs text-muted-foreground text-center mt-4">
        Keep your streak alive by attending events! 🔥
      </p>
    </Card>
  );
};
